import { useToast } from '@mdvip/shared/hooks/use-toast';
import { useErrorTranslation } from '@mdvip/shared/hooks/useErrorTranslation';
import { extractGqlErrorCode } from '@mdvip/shared/lib/gqlErrors';
import { trackException } from '@mdvip/shared/lib/telemetry';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { memberInfoKey } from '@/hooks/member/memberInfoApi';
import { PaymentSetDefaultError, requestSetDefaultPaymentMethod, savedPaymentMethodsKey } from '../paymentApi';
import { EnableMyAutoPayError, requestEnableMyAutoPay } from './autoPayApi';

export interface EnrollAutoPayInput {
  /** The saved card the member chose in the enrollment picker. */
  cardId: string;
  /**
   * True when the chosen card isn't already the member's default. `Member.autoPay` is member-level
   * and always drafts the default card, so making the selection the default is what actually binds
   * AutoPay to it.
   */
  makeDefault: boolean;
}

interface UseEnableMyAutoPayOptions {
  onSuccess?: () => void;
}

/**
 * Enrolls the member in AutoPay from the AutoPay enrollment modal.
 *
 * Two steps, because AutoPay has no per-card linkage: `enableMyAutoPay` is member-scoped and drafts
 * whatever card is currently default, so a selection that isn't already the default is promoted
 * first (`setDefaultPaymentMethod`) and only then enrolled. Set-default failing aborts before
 * enrolling, because enrolling anyway would silently draft the wrong card.
 *
 * Unlike the add/edit-card flows (where enrollment is a secondary effect that must not fail the
 * card save, so it's swallowed to telemetry), enrollment *is* the primary action here: failures
 * surface as a toast and reject, so the modal can stay open and let the member retry.
 */
export function useEnableMyAutoPay({ onSuccess }: UseEnableMyAutoPayOptions = {}) {
  const { toast } = useToast();
  const tError = useErrorTranslation();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ cardId, makeDefault }: EnrollAutoPayInput) => {
      if (makeDefault) {
        await requestSetDefaultPaymentMethod(cardId);
      }
      try {
        await requestEnableMyAutoPay();
      } catch (error) {
        // ALREADY_ENABLED means the desired end state already holds (the modal's own guard reads a
        // cache that can be stale), so it is not a failure worth showing the member.
        if (!(error instanceof EnableMyAutoPayError && error.code === 'ALREADY_ENABLED')) {
          throw error;
        }
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: memberInfoKey });
      // The promoted card demotes the prior default server-side, so the saved-methods list is
      // stale too — including the bank list, since the single-default invariant spans both types.
      void queryClient.invalidateQueries({ queryKey: savedPaymentMethodsKey });
      onSuccess?.();
    },
    onError: (error: unknown) => {
      const code =
        error instanceof PaymentSetDefaultError || error instanceof EnableMyAutoPayError
          ? error.code
          : extractGqlErrorCode(error);
      if (error instanceof Error) {
        trackException(error, { source: 'enableMyAutoPay', context: 'autoPayEnrollment', errorCode: code });
      }
      toast({ title: tError('title'), description: tError(code), variant: 'destructive' });
    },
  });

  return {
    enroll: (input: EnrollAutoPayInput) => mutation.mutateAsync(input),
    isPending: mutation.isPending,
  };
}
